import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { PaperPlaneRight, Paperclip, Robot, User, SpinnerGap, Lightning, Database, RocketLaunch, List, X, CheckCircle, Warning, Sparkle, Receipt, DownloadSimple, ArrowsOut, ArrowsIn, FilePdf, FileText, Eye, Brain, PencilSimple, Check } from "@phosphor-icons/react";
import { agentCreateSession, agentGetSessions, agentGetSession, agentSendMessage, agentTier1cKeyword, agentUploadFile, agentActiveJobs, agentStopSession, setAgentFeesColumns, setAgentFeesApplyOpts, feesCsvPreview, feesCsvPreviewStatus, feesCsvApply, feesCsvHeaders, ingestReconApply, allotmentReconOpen, smReviewOpen } from "../lib/api";
import IngestReconViewer from "./IngestReconViewer";
import CsvReconCard from "./CsvReconCard";
import CsvMappingCard from "./CsvMappingCard";

// IDs of assistant messages already fully revealed — so the typewriter only
// animates genuinely-new narration, never history or background-poll re-renders.
const typedMsgIds = new Set();

// Never display raw tool-call JSON that slipped past backend parsing — strip a
// fenced ```json {…"tool"…}``` block, or a bare {"tool": …, "args": {…}} object,
// from any message before it is rendered or typed.
const stripToolJson = (s) => {
  if (!s) return s;
  let out = s.replace(/```(?:json)?\s*\{[\s\S]*?"tool"[\s\S]*?\}\s*```/gi, "");
  out = out.replace(/\{[^{}]*"tool"\s*:\s*"[^"]+?"[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g, "");
  return out.replace(/\n{3,}/g, "\n\n").trim();
};

// Flowing assistant narration (the only thing that typewrites). User messages,
// structured cards (completion/confirm/recon/applied) and tables are excluded —
// they never type and so never block the typing queue.
const isNarration = (m) => {
  if (!m || m.role !== "assistant") return false;
  const mt = m.metadata?.type;
  if (mt === "job_complete" || mt === "confirm_prompt" || mt === "fees_recon" || mt === "fees_applied" || mt === "ingest_summary" || mt === "tier1c_card") return false;
  if ((m.content || "").includes("|")) return false;   // table → wide card
  return true;
};

export default function AgentChat({ initialSessionId = null, hideSessions = false, feesMode = false }) {
  const [sessions, setSessions] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [jobStatus, setJobStatus] = useState("idle");
  const [jobProgress, setJobProgress] = useState(null);
  const [autoMode, setAutoMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeJobs, setActiveJobs] = useState({ running: 0, queued: 0, total: 0 });
  const [csvCards, setCsvCards] = useState([]);        // deterministic CSV recon summaries (local to view)
  const [reconView, setReconView] = useState(null);    // {ingestId, filename} → full-screen viewer
  // Open the shared reconcile overlay from a completion card's recon hint
  // (same seeding the Ingestion Diary uses: allotment → allot recon, SM → sm-review).
  const openReconFromSummary = async (recon) => {
    if (!recon?.body) return;
    try {
      const { data } = recon.kind === "sm"
        ? await smReviewOpen({ body: recon.body, year: recon.year, round_number: recon.round_number,
                               ...(recon.file_hash ? { file_hash: recon.file_hash } : {}) })
        : await allotmentReconOpen({ counselling_body: recon.body, year: recon.year, round_number: recon.round_number });
      const label = recon.kind === "sm"
        ? `${recon.body} Seat-Matrix · ${recon.year}${recon.round_number ? ` · R${recon.round_number}` : ""} — reconcile colleges`
        : `${recon.body} · ${recon.year} · R${recon.round_number} — reconcile`;
      setReconView({ ingestId: data.ingest_id, filename: label });
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not open reconcile");
    }
  };
  const [csvBusy, setCsvBusy] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);  // {name,size} optimistic card while a file uploads
  const [typedTick, setTypedTick] = useState(0);             // bumps when a message finishes typing → next in queue starts
  const markTyped = () => setTypedTick(t => t + 1);
  const csvFilesRef = useRef({});                       // ingest_id → File (for apply re-read)
  const activeSessionRef = useRef(null);
  const loadingRef = useRef(false);

  // Poll global background-job counts for the running indicator. Also keeps the OPEN
  // session's messages/status in sync every 4s so a response written right around the
  // job-status flip still appears WITHOUT the user having to send another message.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await agentActiveJobs();
        if (!alive) return;
        setActiveJobs(res.data);
        loadSessions();
        const sid = activeSessionRef.current;
        if (sid && !loadingRef.current) {
          const sres = await agentGetSession(sid);
          if (!alive) return;
          setMessages(sres.data.messages || []);
          setJobStatus(sres.data.job_status || "idle");
          setJobProgress(sres.data.job_progress);
        }
      } catch (e) { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  activeSessionRef.current = activeSession;
  loadingRef.current = loading;

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => {
    if (initialSessionId) { setJobStatus("running"); loadSession(initialSessionId); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);
  // Auto-scroll ONLY on user-initiated actions (sending a message, opening a session) —
  // NEVER on the 4s background poll, which otherwise keeps yanking the view down.
  useEffect(() => {
    if (!shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    const el = scrollContainerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if ((jobStatus === "running" || jobStatus === "paused") && activeSession) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await agentGetSession(activeSession);
          const s = res.data;
          setMessages(s.messages || []);
          setJobStatus(s.job_status || "idle");
          setJobProgress(s.job_progress);
          if (s.job_status !== "running" && s.job_status !== "paused") {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (e) { console.error(e); }
      }, 3000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobStatus, activeSession]);

  const loadSessions = async () => {
    try { const res = await agentGetSessions(); setSessions(res.data.sessions || []); } catch (e) {}
  };

  const createNewSession = async () => {
    setSessionLoading(true);
    try {
      const res = await agentCreateSession();
      setActiveSession(res.data.id);
      setMessages([]); setJobStatus("idle"); setJobProgress(null);
      setSessions(prev => [res.data, ...prev]);
      setDrawerOpen(false);
    } catch (e) {}
    setSessionLoading(false);
  };

  const loadSession = async (sid) => {
    setSessionLoading(true);
    try {
      const res = await agentGetSession(sid);
      setActiveSession(sid);
      shouldScrollRef.current = true;
      // History renders instantly — only new narration typewrites.
      (res.data.messages || []).forEach(m => m.id && typedMsgIds.add(m.id));
      setMessages(res.data.messages || []);
      setJobStatus(res.data.job_status || "idle");
      setJobProgress(res.data.job_progress);
      setDrawerOpen(false);
    } catch (e) {}
    setSessionLoading(false);
  };

  const sendMessage = async (text, file) => {
    if (!activeSession || (!text?.trim() && !file)) return;
    setLoading(true);
    // Optimistic attachment card ("Uploading…") so the file shows immediately
    // while the upload/parse round-trip is in flight.
    if (file) { shouldScrollRef.current = true; setPendingUpload({ name: file.name, size: file.size }); }
    try {
      if (file) {
        // Pass only what the user actually typed (may be empty). The backend
        // still records the upload as "[Uploaded: <name>]"; the frontend renders
        // that as the attachment card, so we don't inject filler text that would
        // show up as a redundant bubble under the card.
        await agentUploadFile(activeSession, file, text || "", autoMode);
      } else {
        await agentSendMessage(activeSession, text, autoMode);
      }
      const sRes = await agentGetSession(activeSession);
      shouldScrollRef.current = true;
      setMessages(sRes.data.messages || []);
      setJobStatus(sRes.data.job_status || "idle");
      setJobProgress(sRes.data.job_progress);
      if (sRes.data.job_status === "running") setJobStatus("running");
    } catch (e) { console.error(e); }
    setPendingUpload(null); setLoading(false); setInput("");
  };

  const handleSubmit = (e) => { e.preventDefault(); sendMessage(input); };

  const [stopping, setStopping] = useState(false);
  const stopProcessing = async () => {
    if (!activeSession) return;
    setStopping(true);
    try {
      const res = await agentStopSession(activeSession);
      // Backend hard-cancels the task and marks it cancelled — reflect the stop
      // immediately instead of waiting for the next poll.
      setJobStatus(res?.data?.job_status || "cancelled");
      setJobProgress((p) => ({ ...(p || {}), stage: "Stopped" }));
      const s = await agentGetSession(activeSession);
      setMessages(s.data.messages || []);
    } catch (e) { console.error(e); }
  };
  // Clear the local "stopping" flag once the backend has actually halted.
  useEffect(() => {
    if (jobStatus !== "running" && jobStatus !== "paused") setStopping(false);
  }, [jobStatus]);

  // Elapsed-time ticker for the compact processing line.
  const [procElapsed, setProcElapsed] = useState(0);
  const procStartRef = useRef(null);
  useEffect(() => {
    const active = jobStatus === "running" || jobStatus === "paused";
    if (active) {
      if (!procStartRef.current) procStartRef.current = Date.now();
      const tick = () => setProcElapsed(Math.floor((Date.now() - procStartRef.current) / 1000));
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    }
    procStartRef.current = null;
    setProcElapsed(0);
  }, [jobStatus]);
  const fmtElapsed = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

  // Deterministic CSV branch: a .csv is a fees round-trip file → resolve O(1) via the
  // commons endpoint (NO LLM), persist recon under an ingest_id, and show a summary card
  // + paginated viewer instead of dumping 500+ rows into the chat.
  const handleCsvUpload = async (file) => {
    // The routing probe (feesCsvHeaders) reads the file, which is slow for a
    // PDF. Show the right in-progress affordance while it runs: the PDF
    // attachment card ("Uploading…") for PDFs, the "Resolving CSV rows" pill
    // only for actual CSVs — never the CSV label for a PDF.
    const isPdf = /\.pdf$/i.test(file.name);
    if (isPdf) { shouldScrollRef.current = true; setPendingUpload({ name: file.name, size: file.size }); }
    else setCsvBusy(true);
    try {
      const hf = new FormData();
      hf.append("file", file);
      const { data: h } = await feesCsvHeaders(hf);
      if (h.route === "agent") {
        // PDF that isn't a clean columns table (allotment / per-category matrix / scanned)
        // → hand off to the existing agent chat flow, untouched (which keeps the
        // PDF card up and runs the pipeline).
        setCsvBusy(false);
        sendMessage(input, file);
        return;
      }
      // Clean columns table (CSV, or a table-like PDF) → deterministic CSV cards.
      if (isPdf) setPendingUpload(null);
      if (h.needs_mapping) {
        // stash the file for the mapping card → preview once user confirms mapping
        const mid = `map_${Date.now()}`;
        csvFilesRef.current[mid] = file;
        setCsvCards((prev) => [...prev, { kind: "mapping", mid, filename: file.name, ts: Date.now(), ...h }]);
      } else {
        await runCsvPreview(file, h.suggested);
      }
    } catch (e) {
      if (isPdf) setPendingUpload(null);
      setCsvCards((prev) => [...prev, { error: e?.response?.data?.detail || "File read failed", filename: file.name, ts: Date.now() }]);
    }
    setCsvBusy(false);
  };

  // resolve preview with an (optional) column mapping → replace mapping card with summary card.
  // The preview now runs as a BACKGROUND job (huge PDFs no longer time out / 520): POST returns
  // a job_id, we show a "processing" card and poll until the summary is ready.
  const pollPreview = (jobId, onStage) => new Promise((resolve, reject) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await feesCsvPreviewStatus(jobId);
        if (data.stage && onStage) onStage(data.stage);
        if (data.status === "done") { clearInterval(iv); resolve(data.result); }
        else if (data.status === "error") { clearInterval(iv); reject(new Error(data.error || "Preview failed")); }
        else if (tries > 600) { clearInterval(iv); reject(new Error("Preview timed out")); }
      } catch (e) { clearInterval(iv); reject(e); }
    }, 1500);
  });

  const runCsvPreview = async (file, mapping, mid) => {
    const fd = new FormData();
    fd.append("file", file);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    if (activeSession) fd.append("session_id", activeSession);
    const { data: started } = await feesCsvPreview(fd);
    const jobId = started.job_id;
    const procCard = { kind: "processing", jobId, filename: file.name, stage: "queued", ts: Date.now() };
    setCsvCards((prev) => (mid ? prev.map((c) => (c.mid === mid ? { ...procCard, ts: c.ts || procCard.ts } : c)) : [...prev, procCard]));
    try {
      const data = await pollPreview(jobId, (stage) =>
        setCsvCards((prev) => prev.map((c) => (c.jobId === jobId ? { ...c, stage } : c))));
      csvFilesRef.current[data.ingest_id] = file;
      csvFilesRef.current[`map_${data.ingest_id}`] = mapping || null;
      // Swap the processing card → result, and drop any rebuild card the session-message
      // effect may have added for the same ingest_id (avoids a duplicate on the poll race).
      setCsvCards((prev) => prev
        .filter((c) => c.jobId === jobId || c.ingest_id !== data.ingest_id)
        .map((c) => (c.jobId === jobId ? { ...data, applied: false, ts: c.ts || Date.now() } : c)));
    } catch (e) {
      setCsvCards((prev) => prev.map((c) => (c.jobId === jobId ? { error: e?.message || "Preview failed", filename: file.name, ts: c.ts || Date.now() } : c)));
    }
  };

  const confirmMapping = async (mid, mapping) => {
    const file = csvFilesRef.current[mid];
    if (!file) return;
    setCsvBusy(true);
    try { await runCsvPreview(file, mapping, mid); }
    catch (e) { setCsvCards((prev) => prev.map((c) => c.mid === mid ? { error: e?.response?.data?.detail || "Preview failed", filename: c.filename, ts: c.ts || Date.now() } : c)); }
    setCsvBusy(false);
  };

  const applyCsv = async (ingestId, columns, createNew) => {
    // File-less apply for everyone — rows + reconciled values already live in ingest_recon,
    // so re-parsing the source file would just waste CPU (and 502 on big files in prod).
    const fd = new FormData();
    fd.append("columns", (columns || []).join(","));
    fd.append("create_new", createNew ? "true" : "false");
    const { data } = await ingestReconApply(ingestId, fd);
    setCsvCards((prev) => prev.map((c) => c.ingest_id === ingestId ? { ...c, applied: true, applyResult: data } : c));
    return data;
  };

  // Surface agent-staged column reconciliations (Phase 2) as recon cards, reusing the CSV UI.
  useEffect(() => {
    const seen = new Set(csvCards.map((c) => c.ingest_id));
    const add = [];
    for (const m of messages) {
      const md = m.metadata;
      if (md && md.type === "columns_recon" && md.ingest_id && !seen.has(md.ingest_id)) {
        add.push({ ingest_id: md.ingest_id, filename: md.filename, total: md.total, unmatched: md.unmatched,
                   by_status: md.by_status, by_method: md.by_method, present_columns: md.present_columns, source: "agent",
                   ts: m.timestamp ? new Date(m.timestamp).getTime() : Date.now() });
        seen.add(md.ingest_id);
      }
    }
    if (add.length) setCsvCards((prev) => [...prev, ...add]);
  }, [messages]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isCsv = /\.csv$/i.test(file.name) || file.type === "text/csv";
    const isPdf = /\.pdf$/i.test(file.name);
    // CSV → deterministic columns tool. PDF → probe for a clean columns table; the backend
    // returns route:"agent" for allotment / per-category matrix / scanned PDFs, in which case
    // handleCsvUpload hands off to the normal agent chat flow.
    if (isCsv || isPdf) { handleCsvUpload(file); e.target.value = ""; return; }
    sendMessage(input, file); e.target.value = "";
  };

  // Hide system messages, and defensively hide any assistant message that is
  // just a raw tool-call JSON block (e.g. ```json {"tool": …}```) that slipped
  // through older parsing — it should never be shown to the user.
  const isToolCallJson = (m) => {
    if (m.role !== "assistant") return false;
    const c = (m.content || "").trim().replace(/^```(?:json)?/i, "").replace(/```$/,"").trim();
    return /^\{[\s\S]*"tool"\s*:[\s\S]*\}$/.test(c) && c.length < 400;
  };
  const visibleMessages = messages.filter(m => m.role !== "system" && !isToolCallJson(m));

  // Keep the view pinned to the newest text as the typewriter reveals it.
  const handleTypeScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) el.scrollTop = el.scrollHeight;
  };

  // Clear the optimistic "Uploading…" card the moment the real upload message
  // lands (via the send round-trip OR the background poll), so the header and
  // the card never disagree.
  useEffect(() => {
    if (!pendingUpload) return;
    const landed = messages.some(m => m.role === "user" && (
      m.metadata?.filename === pendingUpload.name ||
      (m.content || "").includes(`[Uploaded: ${pendingUpload.name}]`)
    ));
    if (landed) setPendingUpload(null);
  }, [messages, pendingUpload]);

  const SessionList = () => (
    <>
      <div className="p-3 border-b border-zinc-200">
        <button onClick={createNewSession} disabled={sessionLoading}
          className="w-full h-10 bg-gradient-to-r from-[#6043EF] to-[#6043EF] text-white text-[13px] font-bold uppercase tracking-wider hover:from-[#4A28C2] hover:to-[#5235E0] transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="new-session-btn">
          <Lightning size={15} weight="fill" /> NEW SESSION
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {sessions.map(s => (
          <button key={s.id} onClick={() => loadSession(s.id)}
            className={`w-full text-left px-3 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${activeSession === s.id ? "bg-[#6043EF]/5 border-l-2 border-l-[#6043EF]" : ""}`}
            data-testid={`session-${s.id}`}>
            <span className="font-['IBM_Plex_Mono'] text-[11px] text-zinc-400 block">{new Date(s.created_at).toLocaleDateString()}</span>
            <span className="text-[14px] text-[#09090B] truncate block font-medium">{s.display_name || s.filename || "New Session"}</span>
            <Badge variant="outline" className={`text-[9px] mt-1 ${s.status === "completed" ? "border-[#00C04B] text-[#00C04B]" : s.job_status === "running" ? "border-[#FFC107] text-[#92700C]" : "border-zinc-300 text-zinc-500"}`}>
              {s.job_status === "running" ? "processing" : s.status || "active"}
            </Badge>
          </button>
        ))}
      </div>
    </>
  );

  const inner = (
    <div className={`flex ${(hideSessions || fullscreen) ? "h-full" : "h-[calc(100vh-200px)] sm:h-[calc(100vh-200px)] min-h-[400px]"} relative`} data-testid="agent-chat">
      <div className="flex-1 flex flex-col min-w-0 bg-gradient-to-b from-white to-zinc-50/50">
        {!hideSessions && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-zinc-200 bg-white">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#52525B] truncate">
              {(() => {
                const s = sessions.find(s => s.id === activeSession);
                const name = s?.display_name || s?.filename || "Data Ingestion Agent";
                return name.replace(/^\[?\s*uploaded:\s*/i, "").replace(/\]\s*$/, "").trim() || "Data Ingestion Agent";
              })()}
            </span>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => setFullscreen(v => !v)}
                className="h-8 px-3 flex items-center gap-1.5 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider text-[#09090B] hover:border-[#6043EF] hover:text-[#6043EF] transition-colors"
                data-testid="fullscreen-toggle" title={fullscreen ? "Exit full screen" : "Full screen"}>
                {fullscreen ? <ArrowsIn size={14} weight="bold" /> : <ArrowsOut size={14} weight="bold" />}
                {fullscreen ? "Exit" : "Full screen"}
              </button>
              <button onClick={() => setDrawerOpen(true)}
                className="h-8 px-3 flex items-center gap-1.5 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider text-[#09090B] hover:border-[#6043EF] hover:text-[#6043EF] transition-colors"
                data-testid="sessions-toggle">
                <List size={14} weight="bold" /> Sessions
              </button>
            </div>
          </div>
        )}
        {!activeSession ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-[#6043EF] to-[#6043EF] flex items-center justify-center mx-auto mb-5">
                <Sparkle size={28} weight="fill" className="text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-[#09090B] mb-2">Data Ingestion Agent</h3>
              <p className="text-base text-[#52525B] mb-5">Upload any document — fees, allotments, seat matrices. I'll extract, match, and ingest.</p>
              <button onClick={createNewSession}
                className="h-11 px-7 bg-gradient-to-r from-[#6043EF] to-[#6043EF] text-white text-sm font-bold uppercase tracking-wider hover:from-[#4A28C2] hover:to-[#5235E0] transition-all"
                data-testid="start-session-btn">NEW SESSION</button>
            </div>
          </div>
        ) : (
          <>
            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4" data-testid="chat-messages">
              {visibleMessages.length === 0 && !loading && (
                <div className="flex gap-3 animate-fade-in">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#6043EF] to-[#6043EF] flex items-center justify-center shrink-0 shadow-sm">
                    <Sparkle size={20} weight="fill" className="text-white" />
                  </div>
                  <div className="flex-1 bg-white border border-zinc-200 p-5 sm:p-6 shadow-sm">
                    <p className="text-lg sm:text-xl font-heading font-bold text-[#09090B] mb-3">
                      Upload any file — I'll figure it out.
                    </p>
                    <div className="space-y-1.5 text-[15px] sm:text-base text-[#52525B] font-sans">
                      <p><span className="text-[#00C04B] font-semibold">Fees</span> — CSV, PDF, XLSX, scanned docs</p>
                      <p><span className="text-[#6043EF] font-semibold">NMC Seats</span> — seat matrix PDFs</p>
                      <p><span className="text-[#7C3AED] font-semibold">Allotments</span> — any state, any format</p>
                      <p><span className="text-[#FF6B00] font-semibold">Seat Matrices</span> — any state, any format</p>
                    </div>
                    <p className="mt-4 text-sm text-zinc-400 flex items-center gap-1.5">
                      <CheckCircle size={14} className="text-[#00C04B]" /> 14 state parsers + LLM extraction + vision for scanned docs
                    </p>
                  </div>
                </div>
              )}

              {/* Messages and CSV/columns recon cards live in two separate state
                  arrays (message-based flow vs deterministic columns-import cards).
                  Rendering them as two blocks pinned ALL cards below ALL messages,
                  so with back-to-back files an earlier file's card floated under a
                  later file's narration. Merge both into one timeline sorted by
                  timestamp so each card sits with its own file. */}
              {(() => {
                const lastMsgIdx = visibleMessages.length - 1;
                // One chronological timeline. Array.sort is stable, so equal
                // timestamps keep the input order (messages listed first → a
                // message sorts before a card sharing its timestamp).
                const items = [
                  ...visibleMessages.map((msg, i) => ({
                    _t: "msg", i,
                    ts: msg.timestamp ? new Date(msg.timestamp).getTime() : i,
                    msg })),
                  ...csvCards.map((card, i) => ({
                    _t: "card", i, ts: card.ts || 0, card })),
                ].sort((a, b) => a.ts - b.ts);
                return items.map((it) => {
                  if (it._t === "msg") {
                    return (
                      <MessageBubble key={it.msg.id || `m-${it.i}`} msg={it.msg} index={it.i} feesMode={feesMode} sessionId={activeSession}
                        isLast={it.i === lastMsgIdx}
                        animate={it.i === lastMsgIdx && jobStatus === "running"}
                        canType={visibleMessages.slice(0, it.i).every(m => !isNarration(m) || !m.id || typedMsgIds.has(m.id))}
                        onReconcile={openReconFromSummary}
                        onType={handleTypeScroll} onDone={markTyped} />
                    );
                  }
                  const card = it.card;
                  if (card.kind === "mapping") {
                    return (
                      <CsvMappingCard key={card.mid} card={card}
                        onConfirm={(mapping) => confirmMapping(card.mid, mapping)} />
                    );
                  }
                  if (card.kind === "processing") {
                    return (
                      <div key={card.jobId} className="flex gap-3 animate-fade-in" data-testid={`csv-processing-${card.jobId}`}>
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#059669] to-[#10b981] flex items-center justify-center shrink-0">
                          <SpinnerGap size={18} className="text-white animate-spin" />
                        </div>
                        <div className="bg-white border border-zinc-200 px-4 py-3 rounded-3xl rounded-tl-md shadow-sm text-sm text-[#52525B]">
                          <div className="font-semibold text-[#09090B] truncate max-w-[260px]">{card.filename}</div>
                          <div className="text-xs text-zinc-500 mt-0.5 capitalize">
                            {({ queued: "Queued…", parsing: "Parsing file…", resolving: "Resolving colleges…", saving: "Saving…" }[card.stage]) || "Processing…"}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <CsvReconCard key={card.ingest_id || `csv-${it.i}`} card={card} feesMode={feesMode}
                      onOpen={() => setReconView({ ingestId: card.ingest_id, filename: card.filename })}
                      onApply={applyCsv} />
                  );
                });
              })()}

              {csvBusy && (
                <div className="flex gap-3 animate-fade-in" data-testid="csv-busy">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#059669] to-[#10b981] flex items-center justify-center shrink-0">
                    <SpinnerGap size={18} className="text-white animate-spin" />
                  </div>
                  <div className="bg-white border border-zinc-200 px-4 py-3 rounded-3xl rounded-tl-md shadow-sm text-sm text-[#52525B]">
                    Resolving CSV rows O(1)…
                  </div>
                </div>
              )}

              {pendingUpload && (
                <div className="flex gap-3 animate-fade-in" data-testid="pending-upload">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-2 ring-white flex items-center justify-center shrink-0 bg-gradient-to-br from-[#6043EF] to-[#8b6dff] shadow-sm">
                    <Sparkle size={18} weight="fill" className="text-white" />
                  </div>
                  <div className="max-w-[85%] sm:max-w-[75%]">
                    <div className="flex items-center gap-3 rounded-2xl rounded-tl-md border border-zinc-200 bg-white p-3 pr-4 shadow-sm">
                      <div className={`w-10 h-12 rounded-md flex items-center justify-center shrink-0 ${/\.pdf$/i.test(pendingUpload.name) ? "bg-[#EF4444]/10" : "bg-[#6043EF]/10"}`}>
                        {/\.pdf$/i.test(pendingUpload.name)
                          ? <FilePdf size={26} weight="fill" className="text-[#EF4444]" />
                          : <FileText size={26} weight="fill" className="text-[#6043EF]" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-[#09090B] truncate max-w-[210px]" title={pendingUpload.name}>{pendingUpload.name}</div>
                        <div className="text-[11px] font-['IBM_Plex_Mono'] text-[#6043EF] uppercase tracking-wide flex items-center gap-1.5">
                          <SpinnerGap size={12} className="animate-spin" /> Uploading…
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {loading && !pendingUpload && jobStatus !== "running" && jobStatus !== "paused" && (
                <div className="flex gap-3 animate-fade-in">
                  <div className="w-9 h-9 bg-gradient-to-br from-[#6043EF] to-[#6043EF] flex items-center justify-center shrink-0">
                    <SpinnerGap size={18} className="text-white animate-spin" />
                  </div>
                  <div className="bg-white border border-zinc-200 px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-[#6043EF] rounded-full animate-bounce" style={{animationDelay: "0ms"}} />
                        <div className="w-2 h-2 bg-[#6043EF] rounded-full animate-bounce" style={{animationDelay: "150ms"}} />
                        <div className="w-2 h-2 bg-[#6043EF] rounded-full animate-bounce" style={{animationDelay: "300ms"}} />
                      </div>
                      <span className="text-sm text-[#52525B] font-sans">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Claude-style inline processing status — a pulsating symbol +
                  timer · processing · % · stage, living at the bottom of the
                  chat instead of a fixed top banner. */}
              {(jobStatus === "running" || jobStatus === "paused") && jobProgress && (
                <div className="flex items-center gap-2.5 animate-fade-in py-1" data-testid="job-progress">
                  {jobStatus === "paused"
                    ? <Warning size={17} weight="fill" className="text-[#E65100] shrink-0" />
                    : <PulseStar size={34} className="text-[#6043EF] shrink-0" />}
                  <div className="flex-1 min-w-0 flex items-center gap-2 text-[12px] sm:text-[13px] font-['IBM_Plex_Mono']">
                    {jobStatus === "paused" ? (
                      <span className="font-bold uppercase tracking-wider text-[#E65100]">Awaiting confirmation</span>
                    ) : (
                      <>
                        <span className="tabular-nums font-semibold text-[#6043EF] shrink-0">{fmtElapsed(procElapsed)}</span>
                        <span className="text-[#6043EF]/30 shrink-0">·</span>
                        <span className="uppercase tracking-wider font-bold text-[#6043EF] shrink-0">Processing</span>
                        <span className="text-[#6043EF]/30 shrink-0">·</span>
                        <span className="tabular-nums font-bold text-[#6043EF] shrink-0">{jobProgress.current || 0}%</span>
                        <span className="text-[#6043EF]/30 shrink-0">·</span>
                        <span className="truncate text-[#52525B]">{jobProgress.stage || "Working…"}</span>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={stopProcessing}
                    disabled={stopping}
                    data-testid="stop-processing"
                    title="Stop background processing"
                    className="h-6 px-2 flex items-center gap-1 shrink-0 rounded-md border border-[#EF4444]/40 text-[10px] font-bold uppercase tracking-wider text-[#EF4444] hover:bg-[#EF4444] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X size={11} weight="bold" /> {stopping ? "Stopping…" : "Stop"}
                  </button>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-zinc-200 p-3 sm:p-4 bg-white" data-testid="chat-input-area">
              {activeJobs.total > 0 && (
                <div className="flex items-center gap-2 mb-2.5 px-2.5 py-1.5 bg-[#6043EF]/5 border border-[#6043EF]/15 text-[11px] font-bold text-[#6043EF] w-fit" data-testid="active-jobs-indicator">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6043EF] opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#6043EF]" />
                  </span>
                  <span className="tracking-wide" data-testid="active-jobs-text">
                    {activeJobs.running}/{activeJobs.total} background job{activeJobs.total > 1 ? "s" : ""} running
                    {activeJobs.queued > 0 ? ` · ${activeJobs.queued} queued` : ""}
                  </span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="flex gap-2 items-center">
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={loading || jobStatus === "running"}
                  className="h-11 w-11 border border-zinc-300 flex items-center justify-center text-zinc-500 hover:text-[#6043EF] hover:border-[#6043EF] hover:bg-[#6043EF]/5 transition-all disabled:opacity-50 shrink-0"
                  data-testid="file-upload-btn">
                  <Paperclip size={18} />
                </button>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={handleFileUpload} className="hidden" data-testid="file-input" />
                <button type="button" onClick={() => setAutoMode(!autoMode)}
                  className={`h-11 px-3 flex items-center gap-1.5 shrink-0 transition-all duration-200 ${
                    autoMode ? "bg-gradient-to-r from-[#6043EF] to-[#6043EF] text-white border border-[#6043EF]" : "bg-zinc-100 text-zinc-400 border border-zinc-200 hover:border-zinc-300"
                  }`}
                  title={autoMode ? "Auto mode: applies without confirmation" : "HITL mode: shows preview before applying"}
                  data-testid="auto-mode-toggle">
                  <RocketLaunch size={14} weight={autoMode ? "fill" : "regular"} />
                  <span className="text-[10px] font-bold tracking-wider hidden sm:inline">Auto</span>
                  <div className={`w-7 h-4 rounded-full relative transition-colors duration-200 ${autoMode ? "bg-white/30" : "bg-zinc-300"}`}>
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-200 ${autoMode ? "left-3.5 bg-white" : "left-0.5 bg-white"}`} />
                  </div>
                </button>
                <Input value={input} onChange={(e) => setInput(e.target.value)}
                  placeholder={jobStatus === "paused" ? "Type 'yes' to apply..." : "Ask anything or upload a file..."}
                  disabled={loading}
                  className="flex-1 min-w-0 h-11 border-zinc-300 focus:border-[#6043EF] font-sans text-base sm:text-lg" data-testid="chat-text-input" />
                <button type="submit" disabled={loading || !input.trim()}
                  className="h-11 w-11 bg-gradient-to-r from-[#6043EF] to-[#6043EF] text-white flex items-center justify-center hover:from-[#4A28C2] hover:to-[#5235E0] transition-all disabled:opacity-50 shrink-0"
                  data-testid="chat-send-btn">
                  <PaperPlaneRight size={16} weight="bold" />
                </button>
              </form>
            </div>
          </>
        )}
      </div>

      {!hideSessions && (
        <>
          {drawerOpen && (
            <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setDrawerOpen(false)} data-testid="sessions-backdrop" />
          )}
          <div
            className={`fixed top-0 right-0 h-full z-50 w-72 max-w-[85vw] bg-white border-l border-zinc-200 flex flex-col shadow-[-8px_0_30px_-12px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-out ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
            data-testid="sessions-drawer"
          >
            <div className="flex items-center justify-between p-3 border-b border-zinc-200">
              <span className="text-xs font-bold uppercase tracking-wider text-[#09090B]">Sessions</span>
              <button onClick={() => setDrawerOpen(false)} className="w-7 h-7 flex items-center justify-center hover:bg-zinc-100 rounded" data-testid="sessions-drawer-close">
                <X size={16} weight="bold" />
              </button>
            </div>
            <SessionList />
          </div>
        </>
      )}

      {reconView && (
        <IngestReconViewer ingestId={reconView.ingestId} filename={reconView.filename}
          onClose={() => setReconView(null)}
          onSummaryChange={(s) => setCsvCards((prev) => prev.map((c) => c.ingest_id === s.ingest_id
            ? { ...c, total: s.total, unmatched: s.unmatched, by_status: s.by_status, by_method: s.by_method }
            : c))}
          onApplied={(iid, res) => setCsvCards((prev) => prev.map((c) => c.ingest_id === iid
            ? { ...c, applied: true, applyResult: res }
            : c))} />
      )}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="agent-chat-fullscreen">
        {inner}
      </div>
    );
  }
  return inner;
}

// Tier-1c learning card: a PDF-template-styled preview rendered when the agent's
// classifier lands in the LLM rung (body known, no deterministic type signal). It
// makes the rung visible and previews exactly what will be learned.
//   head   → the NEW keyword being learned + already-known keywords as pills
//   header → the document masthead, rendered like a letterhead + classification chips
//   body   → a few sample data rows (monospace, PDF-table feel)
//   footer → the LLM's one-line reason, or empty
function TierOneCCard({ card, index, sessionId, msgId }) {
  const {
    body, year, round, pdf_type, kind,
    new_keyword, existing_keywords = [], header_lines = [], sample_rows = [],
    confidence, reason,
  } = card || {};
  const typeLabel = (pdf_type || kind || "").replace(/_/g, " ");
  const clip = (s, n = 120) => (s && s.length > n ? s.slice(0, n) + "…" : s);

  // Inline keyword edit — the LLM can propose a round-specific phrase (e.g. "THIRD
  // ROUND VACANT") that would only match one round. The user picks a round-agnostic
  // substring of the header; on submit the server re-validates it against the masthead
  // (must be present + 1-4 words + not generic) before it's accepted for learning.
  const [kw, setKw] = useState(new_keyword || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(new_keyword || "");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submitKw = async () => {
    const v = (draft || "").trim();
    if (!v) { setErr("Keyword can't be empty"); return; }
    setSaving(true); setErr("");
    try {
      const { data } = await agentTier1cKeyword(sessionId, msgId, v);
      if (data?.ok) { setKw(data.keyword); setEditing(false); }
      else setErr(data?.reason || "Keyword must appear in the header above");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update keyword");
    } finally { setSaving(false); }
  };
  return (
    <div className="flex gap-3 animate-fade-in" data-testid={`msg-${index}`}>
      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-2 ring-white flex items-center justify-center shrink-0 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] shadow-sm">
        <Brain size={18} weight="fill" className="text-white" />
      </div>
      <div className="max-w-[92%] sm:max-w-[86%] w-full" data-testid="tier1c-card">
        {/* PDF "page" */}
        <div className="rounded-xl border border-violet-200 bg-white shadow-md overflow-hidden">
          {/* HEADER — letterhead + classification chips (TOP) */}
          <div className="border-b border-dashed border-zinc-200 px-4 py-3 text-center bg-[linear-gradient(to_bottom,#fafafa,#ffffff)]">
            {header_lines.length
              ? header_lines.map((l, i) => (
                  <div key={i} className={i === 0 ? "text-[13px] font-bold leading-tight text-zinc-800" : "text-[11px] leading-snug text-zinc-500"}>{l}</div>
                ))
              : <div className="text-[11px] italic text-zinc-400">— no header text —</div>}
            <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
              {typeLabel && <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">{typeLabel}</span>}
              {body && <span className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700">{body}</span>}
              {year && <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">{year}</span>}
              {round ? <span className="rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-bold text-sky-700">R{round}</span> : null}
              {confidence && <span className="rounded-md border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-zinc-500">{confidence}</span>}
            </div>
          </div>
          {/* LEARNING ROW — editable new keyword + already-known keywords */}
          <div className="border-b border-violet-100 bg-gradient-to-r from-violet-50 to-fuchsia-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Brain size={15} weight="fill" className="text-violet-600 shrink-0" />
                <span className="text-[11px] font-bold uppercase tracking-wide text-violet-700 shrink-0">Tier-1c · Learning</span>
                {editing ? (
                  <span className="ml-1 flex items-center gap-1">
                    <input
                      autoFocus value={draft} disabled={saving}
                      onChange={(e) => { setDraft(e.target.value); setErr(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") submitKw(); if (e.key === "Escape") { setEditing(false); setDraft(kw); setErr(""); } }}
                      className="w-44 rounded-full border border-violet-300 bg-white px-2 py-0.5 text-[11px] font-bold uppercase text-violet-700 outline-none focus:ring-2 focus:ring-violet-300"
                      data-testid="tier1c-keyword-input" />
                    <button onClick={submitKw} disabled={saving} title="Validate & save" className="rounded-full bg-violet-600 p-1 text-white hover:bg-violet-700 disabled:opacity-50" data-testid="tier1c-keyword-save">
                      {saving ? <SpinnerGap size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
                    </button>
                    <button onClick={() => { setEditing(false); setDraft(kw); setErr(""); }} disabled={saving} title="Cancel" className="rounded-full border border-violet-200 bg-white p-1 text-violet-500 hover:bg-violet-50">
                      <X size={12} weight="bold" />
                    </button>
                  </span>
                ) : (
                  kw && (
                    <button onClick={() => { setDraft(kw); setEditing(true); }} title="Edit keyword — must appear in the header"
                      className="ml-1 group flex items-center gap-1 rounded-full bg-violet-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm hover:bg-violet-700" data-testid="tier1c-keyword-pill">
                      <span className="truncate max-w-[220px]">{kw}</span>
                      <PencilSimple size={11} weight="bold" className="opacity-70 group-hover:opacity-100" />
                    </button>
                  )
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[10px] font-semibold uppercase text-violet-400">Known</span>
                {existing_keywords.length
                  ? existing_keywords.slice(0, 4).map((k, i) => (
                      <span key={i} className="rounded-full border border-violet-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-violet-600">{k}</span>
                    ))
                  : <span className="text-[10px] italic text-zinc-400">none yet</span>}
              </div>
            </div>
            {err && <div className="mt-1 pl-6 text-[10px] font-medium text-rose-500" data-testid="tier1c-keyword-err">{err}</div>}
          </div>
          {/* BODY — sample data rows */}
          <div className="px-3 py-2.5">
            <div className="mb-1 text-[9px] font-bold uppercase tracking-wider text-zinc-400">Sample rows</div>
            {sample_rows.length
              ? (
                <div className="space-y-0.5 overflow-x-auto">
                  {sample_rows.slice(0, 3).map((r, i) => (
                    <div key={i} className="whitespace-nowrap border-b border-zinc-50 py-0.5 font-['IBM_Plex_Mono'] text-[10px] leading-relaxed text-zinc-600">{clip(r)}</div>
                  ))}
                </div>
              )
              : <div className="text-[10px] italic text-zinc-400">— no tabular rows extracted —</div>}
          </div>
          {/* FOOTER — reason, or empty */}
          <div className="border-t border-dashed border-zinc-200 px-4 py-1.5 text-center">
            <span className="text-[9px] italic text-zinc-300">{reason || " "}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, index, feesMode, sessionId, animate = false, isLast = false, canType = false, onType, onDone, onReconcile }) {
  const isUser = msg.role === "user";
  const [zoomThumb, setZoomThumb] = useState(false);
  const [reconBusy, setReconBusy] = useState(false);
  const content = isUser ? (msg.content || "") : stripToolJson(msg.content || "");
  const mtype = msg.metadata?.type;
  const isComplete = mtype === "job_complete";
  const isConfirm = mtype === "confirm_prompt";
  const isRecon = mtype === "fees_recon";
  const isApplied = mtype === "fees_applied";
  const isSummary = mtype === "ingest_summary";
  const wide = isRecon || isApplied || (!isUser && content.includes("|"));

  // Tier-1c learning card — a PDF-styled preview that makes it obvious we landed
  // in the LLM classification/learning rung (vs a deterministic Tier-1a hit).
  if (mtype === "tier1c_card" && msg.metadata?.tier1c_card) {
    return <TierOneCCard card={msg.metadata.tier1c_card} index={index} sessionId={sessionId} msgId={msg.id} />;
  }

  // Ingest completion summary → a green "sticky note" bubble (with a check
  // avatar), so the final result stands out from the flowing narration.
  if (isSummary) {
    return (
      <div className="flex gap-3 animate-fade-in" data-testid={`msg-${index}`}>
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-2 ring-white flex items-center justify-center shrink-0 bg-gradient-to-br from-[#00C04B] to-[#00E676] shadow-sm">
          <CheckCircle size={18} weight="fill" className="text-white" />
        </div>
        <div className="max-w-[85%] sm:max-w-[80%] rounded-2xl rounded-tl-md border border-[#00C04B]/45 bg-gradient-to-br from-[#DCFCE7] to-[#BBF7D0] px-4 py-3 shadow-md text-[15px] leading-relaxed text-[#14532D]">
          <FormattedContent content={content} isUser={false} animate={false} />
        </div>
      </div>
    );
  }

  // Plain assistant narration (status lines, page-by-page progress, validation
  // notes) renders like a Claude session: flowing text, no avatar, no card
  // boundary. Structured messages keep their card treatment: user input,
  // completion, confirm prompts, recon/applied blocks, and anything with a
  // table (wide).
  const isPlain = !isUser && !isComplete && !isConfirm && !isRecon && !isApplied && !wide;
  if (isPlain) {
    // Newly-arrived system/assistant narration typewrites at ~250 WPM, one
    // message at a time: a message types only once every earlier message has
    // finished (canType), so a batch that lands together reveals sequentially
    // instead of dumping. History / already-typed messages render instantly;
    // messages still waiting their turn stay hidden until then.
    const typed = !msg.id || typedMsgIds.has(msg.id);
    if (!typed && !canType) return <div className="hidden" data-testid={`msg-${index}`} />;
    const typeIt = !typed;
    return (
      <div className="animate-fade-in group -my-1.5" data-testid={`msg-${index}`}>
        <div className="text-[15px] sm:text-base font-sans leading-relaxed whitespace-pre-wrap break-words text-[#27272A]">
          {typeIt
            ? <Typewriter text={content} isUser={false} animate={animate} msgId={msg.id} onType={onType} onDone={onDone} />
            : <FormattedContent content={content} isUser={false} animate={animate} />}
        </div>
        <span className="text-[10px] font-['IBM_Plex_Mono'] mt-0.5 block text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity">
          {new Date(msg.timestamp).toLocaleTimeString()}
        </span>
      </div>
    );
  }

  // File upload → beautified attachment card (PDF icon + name + type/size),
  // right-aligned like a user message, instead of a "[Uploaded: …]" text
  // bubble. Falls back to parsing the "[Uploaded: name]" prefix for older
  // messages that predate the file_upload metadata.
  const uploadName = msg.metadata?.filename || (content.match(/^\[Uploaded:\s*(.+?)\]/)?.[1] || "").trim();
  if (isUser && uploadName) {
    let extra = content.replace(/^\[Uploaded:\s*.+?\]\s*/, "").trim();
    // Drop synthetic/duplicate remnants so only genuinely-typed text shows as a
    // bubble under the card (older messages may still carry these).
    if (/^Uploaded:\s/i.test(extra) || extra === "Please analyze this file.") extra = "";
    const ext = (uploadName.split(".").pop() || "").toUpperCase();
    const isPdf = ext === "PDF";
    const sizeKb = msg.metadata?.size
      ? (msg.metadata.size >= 1048576
          ? `${(msg.metadata.size / 1048576).toFixed(1)} MB`
          : `${Math.max(1, Math.round(msg.metadata.size / 1024))} KB`)
      : null;
    return (
      <div className="flex gap-3 animate-fade-in" data-testid={`msg-${index}`}>
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-2 ring-white flex items-center justify-center shrink-0 bg-gradient-to-br from-[#6043EF] to-[#8b6dff] shadow-sm">
          <Sparkle size={18} weight="fill" className="text-white" />
        </div>
        <div className="max-w-[85%] sm:max-w-[75%] flex flex-col items-start gap-1.5">
          <div className="rounded-2xl rounded-tl-md border border-zinc-200 bg-white p-3 shadow-sm" data-testid="file-upload-card">
            <div className="flex items-center gap-3 pr-1">
              <div className={`w-10 h-12 rounded-md flex items-center justify-center shrink-0 ${isPdf ? "bg-[#EF4444]/10" : "bg-[#6043EF]/10"}`}>
                {isPdf ? <FilePdf size={26} weight="fill" className="text-[#EF4444]" />
                       : <FileText size={26} weight="fill" className="text-[#6043EF]" />}
              </div>
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-[#09090B] truncate max-w-[210px]" title={uploadName}>{uploadName}</div>
                <div className="text-[11px] font-['IBM_Plex_Mono'] text-zinc-400 uppercase tracking-wide">
                  {ext || "FILE"}{sizeKb ? ` · ${sizeKb}` : ""}
                </div>
              </div>
            </div>
            {msg.metadata?.thumbnail && (
              <button type="button" onClick={() => setZoomThumb(true)}
                 className="mt-3 block rounded-lg border border-zinc-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-zoom-in p-0"
                 title="Page 1 preview — click to enlarge" data-testid="file-upload-thumb">
                <img src={msg.metadata.thumbnail} alt="Page 1 preview"
                     className="block w-auto max-h-32" />
              </button>
            )}
          </div>
          {zoomThumb && msg.metadata?.thumbnail && createPortal(
            <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4 animate-fade-in"
                 onClick={() => setZoomThumb(false)} data-testid="file-upload-thumb-modal">
              <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => setZoomThumb(false)}
                        className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-white text-zinc-700 shadow-md flex items-center justify-center hover:bg-zinc-100"
                        title="Close" data-testid="file-upload-thumb-close" aria-label="Close preview">
                  <X size={18} weight="bold" />
                </button>
                <img src={msg.metadata.thumbnail} alt="Page 1 preview (enlarged)"
                     className="block rounded-lg shadow-2xl max-w-full max-h-[90vh] w-auto h-auto bg-white" />
              </div>
            </div>,
            document.body
          )}
          {extra && (
            <div className="max-w-full break-words rounded-2xl rounded-tl-md border border-[#FCD34D] bg-gradient-to-br from-[#FEF9C3] to-[#FDE68A] px-4 py-2 text-[16px] italic font-['Fraunces'] text-[#713F12] shadow-sm">
              {extra}
            </div>
          )}
          <span className="text-[10px] font-['IBM_Plex_Mono'] text-zinc-300">
            {new Date(msg.timestamp).toLocaleTimeString()}
          </span>
        </div>
      </div>
    );
  }

  const bubbleStyle = isUser
    ? "bg-gradient-to-br from-[#FEF9C3] to-[#FDE68A] text-[#713F12] border-[#FCD34D] shadow-md"
    : isComplete
      ? "bg-gradient-to-br from-[#00C04B]/5 to-[#00E676]/10 border-[#00C04B]/40 text-[#09090B] shadow-sm"
      : isConfirm || isRecon
        ? "bg-gradient-to-br from-[#FFC107]/5 to-[#FF9800]/10 border-[#FFC107]/40 text-[#09090B] shadow-sm"
        : "bg-white border-zinc-200 text-[#09090B] shadow-sm";
  // creative rounded bubble with a little tail toward its avatar
  const radius = isUser ? "rounded-2xl rounded-tr-sm" : "rounded-3xl rounded-tl-md";

  return (
    <div className={`flex gap-3 animate-fade-in ${isUser ? "flex-row-reverse" : ""}`} data-testid={`msg-${index}`}>
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-2 ring-white flex items-center justify-center shrink-0 ${
        isUser ? "bg-gradient-to-br from-[#09090B] to-[#333] shadow-sm"
               : isComplete ? "bg-gradient-to-br from-[#00C04B] to-[#00E676] shadow-sm"
               : feesMode ? "bg-gradient-to-br from-[#059669] to-[#10b981] shadow-sm"
               : "bg-gradient-to-br from-[#6043EF] to-[#8b6dff] shadow-sm"
      }`}>
        {isUser ? <User size={18} className="text-white" />
                 : isComplete ? <CheckCircle size={18} weight="fill" className="text-white" />
                 : feesMode ? <Receipt size={18} weight="fill" className="text-white" />
                 : <Sparkle size={18} weight="fill" className="text-white" />}
      </div>
      <div className={`${wide ? "max-w-[99%] sm:max-w-[94%] w-full" : "max-w-[85%] sm:max-w-[75%]"} border p-4 sm:p-5 ${radius} ${bubbleStyle}`}>
        <div className={`leading-relaxed whitespace-pre-wrap break-words ${isUser ? "text-[17px] italic font-['Fraunces']" : "text-[15px] sm:text-base font-sans"}`}>
          <FormattedContent content={content} isUser={isUser} animate={animate} />
        </div>
        {isRecon && <FeesReconTable recon={msg.metadata.recon} sessionId={sessionId} msgId={msg.id || `idx-${index}`} />}
        {isApplied && <FeesAppliedBlock meta={msg.metadata} />}
        {isComplete && msg.metadata?.recon?.body && onReconcile && (() => {
          const _recon = msg.metadata.recon;
          const _um = _recon.unmatched;
          const known = _um !== undefined && _um !== null;
          const hasUnmatched = !known || _um > 0;
          // Allotment recon overlay is UNMATCHED-ONLY (matched rows are pruned),
          // so when everything matched there is nothing to open — show an inline
          // "all matched" confirmation instead of a button to an empty overlay.
          // SM overlay seeds matched+unmatched, so "View Data" is meaningful there.
          if (_recon.kind !== "sm" && known && _um === 0) {
            return (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-[13px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                <CheckCircle size={15} weight="fill" />
                All {msg.metadata?.colleges ?? ""} colleges matched — nothing to reconcile
              </div>
            );
          }
          return (
            <button type="button" data-testid="job-complete-reconcile"
              disabled={reconBusy}
              onClick={async () => { setReconBusy(true); try { await onReconcile(_recon); } finally { setReconBusy(false); } }}
              className={`mt-3 inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[13px] font-semibold shadow-sm disabled:opacity-60 ${hasUnmatched ? "bg-[#00C04B] text-white hover:bg-[#00a641]" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 ring-1 ring-zinc-200"}`}>
              {reconBusy ? <SpinnerGap size={15} className="animate-spin" /> : (hasUnmatched ? <Database size={15} weight="fill" /> : <Eye size={15} weight="fill" />)}
              {reconBusy ? "Opening…" : (hasUnmatched ? "Reconcile" : "View Data")}
            </button>
          );
        })()}
        <span className={`text-[10px] sm:text-[11px] font-['IBM_Plex_Mono'] mt-2 block ${isUser ? "text-[#A16207]/70" : "text-zinc-400"}`}>
          {new Date(msg.timestamp).toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
}

const FEE_HEAD = { govt_fees: "Govt", private_fees: "Private", other_fees: "Mgmt/Other", nri_fees: "NRI" };
const fmtNum = (v) => (v === null || v === undefined || v === "") ? "" : (typeof v === "number" ? v.toLocaleString("en-IN") : String(v));

function FeesReconTable({ recon, sessionId, msgId }) {
  const cols = recon?.columns || [];
  // A quota column is selectable only if at least one row carries a value for it (1a).
  const presentCols = cols.filter((c) => (recon?.rows || []).some((r) => r[c] !== null && r[c] !== undefined && r[c] !== ""));
  const [selected, setSelected] = useState(() => new Set(presentCols));
  const [saving, setSaving] = useState(false);
  const [savedAll, setSavedAll] = useState(true);
  const [year, setYear] = useState((recon?.applying?.year) || (recon?.detected?.year) || "");
  const [course, setCourse] = useState((recon?.applying?.course) || (recon?.detected?.course) || "MBBS");
  const [optSaved, setOptSaved] = useState(false);
  const [skipped, setSkipped] = useState(() => new Set());
  const [overrides, setOverrides] = useState({});   // {ridx: {field: value|null}} — drag-moved cells
  const [drag, setDrag] = useState(null);           // {ridx, field, value} while dragging

  // Reset local edit state ONLY when a genuinely new recon message appears (stable msgId),
  // NOT on every background poll (which hands us a fresh recon object with identical content).
  useEffect(() => {
    setSelected(new Set(presentCols));
    setYear((recon?.applying?.year) || (recon?.detected?.year) || "");
    setCourse((recon?.applying?.course) || (recon?.detected?.course) || "MBBS");
    setSkipped(new Set());
    setOverrides({});
    setDrag(null);
    /* eslint-disable-next-line */
  }, [msgId]);

  if (!recon?.rows?.length) return null;
  let lastPage = null;
  const methodLabel = recon.method_label || recon.method;

  const persist = async (next) => {
    if (!sessionId) return;
    setSaving(true);
    try {
      await setAgentFeesColumns(sessionId, Array.from(next));
      setSavedAll(next.size === presentCols.length);
    } catch (e) { /* best-effort; apply still defaults to all present */ }
    setSaving(false);
  };
  const toggle = (c) => {
    const next = new Set(selected);
    next.has(c) ? next.delete(c) : next.add(c);
    if (next.size === 0) return; // never allow zero columns
    setSelected(next);
    persist(next);
  };

  const applying = recon.applying || {};
  const detected = recon.detected || {};
  const saveOpts = async (patch) => {
    if (!sessionId) return;
    try { await setAgentFeesApplyOpts(sessionId, patch); setOptSaved(true); } catch (e) {}
  };
  const yearMismatch = detected.year && year && detected.year !== year;
  const courseMismatch = detected.course && course && detected.course !== course;
  const toggleSkip = (ridx) => {
    if (ridx == null) return;
    const next = new Set(skipped);
    next.has(ridx) ? next.delete(ridx) : next.add(ridx);
    setSkipped(next);
    saveOpts({ skip_rows: Array.from(next) });
  };

  // Effective cell value = drag-override for this row/field if set, else the extracted value.
  const cellVal = (r, field) => {
    const ov = overrides[r.ridx];
    if (ov && Object.prototype.hasOwnProperty.call(ov, field)) return ov[field];
    return r[field];
  };
  const hasVal = (v) => v !== null && v !== undefined && v !== "";
  // Move a value from one quota column to a BLANK one within the SAME row.
  const moveCell = (ridx, fromField, toField) => {
    if (ridx == null || fromField === toField) return;
    const row = recon.rows.find((x) => x.ridx === ridx);
    if (!row) return;
    if (hasVal(cellVal(row, toField))) return;           // blank-target only
    const val = cellVal(row, fromField);
    if (!hasVal(val)) return;
    const next = { ...overrides, [ridx]: { ...(overrides[ridx] || {}), [fromField]: null, [toField]: val } };
    setOverrides(next);
    saveOpts({ fee_overrides: next });
    setDrag(null);
  };

  return (
    <div className="mt-3 rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="fees-recon-table">
      {methodLabel && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#6043EF]/5 border-b border-zinc-200" data-testid="fees-recon-method">
          <span className="text-[9px] font-black uppercase tracking-[0.18em] text-[#6043EF] whitespace-nowrap">Extraction path</span>
          <span className="text-[11px] font-semibold text-[#09090B]">{methodLabel}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 bg-zinc-50 border-b border-zinc-200" data-testid="fees-recon-applyopts">
        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-zinc-500 whitespace-nowrap">Apply under</span>
        <label className="inline-flex items-center gap-1.5">
          <span className="text-[10px] font-bold text-zinc-500 uppercase">Year</span>
          <input value={year} onChange={(e) => setYear(e.target.value)} onBlur={() => saveOpts({ year })} placeholder="2026-27"
            data-testid="fees-apply-year"
            className={`w-24 h-7 px-2 rounded border text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40 ${yearMismatch ? "border-amber-400 bg-amber-50" : "border-zinc-300"}`} />
        </label>
        <label className="inline-flex items-center gap-1.5">
          <span className="text-[10px] font-bold text-zinc-500 uppercase">Course</span>
          <select value={course} onChange={(e) => { setCourse(e.target.value); saveOpts({ course: e.target.value }); }}
            data-testid="fees-apply-course"
            className={`h-7 px-2 rounded border text-[11px] font-semibold focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40 ${courseMismatch ? "border-amber-400 bg-amber-50" : "border-zinc-300"}`}>
            <option value="MBBS">MBBS</option>
            <option value="BDS">BDS</option>
          </select>
        </label>
        {(detected.state || detected.year || detected.course) && (
          <span className="text-[10px] text-zinc-400 whitespace-nowrap" data-testid="fees-detected">
            detected in doc: {detected.state || "—"} · {detected.year || "—"} · {detected.course || "—"}
          </span>
        )}
        {(yearMismatch || courseMismatch) && (
          <span className="text-[10px] font-bold text-amber-600 whitespace-nowrap" data-testid="fees-apply-mismatch">⚠ differs from document — fix if wrong</span>
        )}
        {optSaved && <span className="text-[10px] text-emerald-600 ml-auto">saved</span>}
      </div>
      {presentCols.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 bg-zinc-50 border-b border-zinc-200" data-testid="fees-recon-columns">
          <span className="text-[9px] font-black uppercase tracking-[0.18em] text-zinc-500 whitespace-nowrap">Apply columns</span>
          {presentCols.map((c) => (
            <label key={c} className="inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid={`fees-col-toggle-${c}`}>
              <input type="checkbox" checked={selected.has(c)} onChange={() => toggle(c)}
                className="w-3.5 h-3.5 accent-[#059669] cursor-pointer" />
              <span className={`text-[11px] font-semibold ${selected.has(c) ? "text-[#09090B]" : "text-zinc-400 line-through"}`}>{FEE_HEAD[c] || c}</span>
            </label>
          ))}
          <span className="text-[10px] text-zinc-400 ml-auto whitespace-nowrap">
            {saving ? "saving…" : savedAll ? "all columns will be written" : `only ${selected.size} column(s) will be written · reply 'yes' to apply`}
          </span>
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="w-full text-left text-[12px] border-collapse font-['IBM_Plex_Mono']">
        <thead>
          <tr className="bg-zinc-100 text-zinc-600">
            <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">#</th>
            <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Code</th>
            <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap min-w-[200px]">College</th>
            {cols.map((c) => <th key={c} className={`text-left font-bold px-2.5 py-2 whitespace-nowrap ${presentCols.includes(c) && !selected.has(c) ? "text-zinc-300 line-through" : ""}`}>{FEE_HEAD[c] || c}</th>)}
            <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Matched</th>
            <th className="text-center font-bold px-2.5 py-2 whitespace-nowrap" title="Skip this row — won't be applied, but is included in the CSV">Skip</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {recon.rows.map((r, i) => {
            const showPage = r.page != null && r.page !== lastPage;
            if (r.page != null) lastPage = r.page;
            const isSkipped = skipped.has(r.ridx);
            return [
              showPage ? (
                <tr key={`p${i}`} data-testid={`recon-page-${r.page}`}>
                  <td colSpan={5 + cols.length} className="px-3 py-1.5 bg-[#6043EF]/5 text-[#6043EF] text-[10px] font-black uppercase tracking-[0.2em] text-center">—— Page {r.page} ——</td>
                </tr>
              ) : null,
              <tr key={i} className={isSkipped ? "bg-zinc-100 opacity-60" : (r.matched ? "bg-emerald-50/40" : "bg-rose-50/40")}>
                <td className="px-2.5 py-1.5 text-zinc-400">{r.sno}</td>
                <td className="px-2.5 py-1.5 text-[#6043EF] whitespace-nowrap">{r.code || "—"}</td>
                <td className={`px-2.5 py-1.5 text-[#09090B] ${isSkipped ? "line-through" : ""}`}>{r.name || "—"}</td>
                {cols.map((c) => {
                  const v = cellVal(r, c);
                  const filled = hasVal(v);
                  const dimmed = isSkipped || (presentCols.includes(c) && !selected.has(c));
                  const isDragSrc = drag && drag.ridx === r.ridx && drag.field === c;
                  const isDropOk = drag && drag.ridx === r.ridx && drag.field !== c && !filled;
                  return (
                    <td key={c}
                      draggable={filled && !isSkipped}
                      onDragStart={filled && !isSkipped ? (e) => { setDrag({ ridx: r.ridx, field: c, value: v }); e.dataTransfer.effectAllowed = "move"; } : undefined}
                      onDragEnd={() => setDrag(null)}
                      onDragOver={isDropOk ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } : undefined}
                      onDrop={isDropOk ? (e) => { e.preventDefault(); moveCell(drag.ridx, drag.field, c); } : undefined}
                      title={filled && !isSkipped ? "Drag to move this fee into a blank column in the same row" : (isDropOk ? "Drop here" : undefined)}
                      data-testid={`fees-cell-${c}-${r.ridx}`}
                      className={`px-2.5 py-1.5 whitespace-nowrap ${dimmed ? "text-zinc-300 line-through" : ""} ${filled && !isSkipped ? "cursor-grab" : ""} ${isDragSrc ? "opacity-40" : ""} ${isDropOk ? "ring-2 ring-inset ring-[#6043EF]/50 bg-[#6043EF]/5" : ""}`}>
                      {fmtNum(v)}
                    </td>
                  );
                })}
                <td className="px-2.5 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${r.matched ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{r.matched ? "yes" : "no"}</span>
                </td>
                <td className="px-2.5 py-1.5 text-center">
                  <input type="checkbox" checked={isSkipped} onChange={() => toggleSkip(r.ridx)}
                    title="Skip this row (won't be applied; still in CSV)"
                    data-testid={`fees-skip-${r.ridx}`}
                    className="w-3.5 h-3.5 accent-rose-500 cursor-pointer" />
                </td>
              </tr>,
            ];
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function FeesAppliedBlock({ meta }) {
  const cols = meta.columns || [];
  const rows = meta.unmatched || [];
  const nSkip = (meta.counts && meta.counts.skipped) || rows.filter((r) => r.status === "skipped").length;
  const nUn = (meta.counts && meta.counts.unmatched) || rows.filter((r) => r.status === "unmatched").length;
  const download = () => {
    const blob = new Blob([meta.csv || ""], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "unmatched_skipped_fees.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  if (!rows.length) return null;
  return (
    <div className="mt-3" data-testid="fees-applied-block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-black uppercase tracking-wider text-rose-600">
          Not written — {nUn} unmatched{nSkip ? ` · ${nSkip} skipped` : ""} ({rows.length})
        </span>
        <button onClick={download} data-testid="download-unmatched-csv"
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-[#09090B] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-black">
          <DownloadSimple size={12} weight="bold" /> CSV
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-rose-200 bg-white">
        <table className="w-full text-left text-[12px] border-collapse font-['IBM_Plex_Mono']">
          <thead>
            <tr className="bg-rose-50 text-rose-700">
              <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Status</th>
              <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Code</th>
              <th className="text-left font-bold px-2.5 py-2 min-w-[200px]">College</th>
              {cols.map((c) => <th key={c} className="text-left font-bold px-2.5 py-2 whitespace-nowrap">{FEE_HEAD[c] || c}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-zinc-50">
                <td className="px-2.5 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${r.status === "skipped" ? "bg-zinc-200 text-zinc-600" : "bg-rose-100 text-rose-700"}`}>{r.status || "unmatched"}</span>
                </td>
                <td className="px-2.5 py-1.5 text-zinc-400 whitespace-nowrap">{r.code || "—"}</td>
                <td className="px-2.5 py-1.5 text-[#09090B]">{r.name || "—"}</td>
                {cols.map((c) => <td key={c} className="px-2.5 py-1.5 whitespace-nowrap">{fmtNum(r[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-zinc-400 mt-1.5">Skipped rows were intentionally excluded. Assign unmatched ones from the <b>Ingestion Diary → detail</b> (lite-search → Assign).</p>
    </div>
  );
}

// Claude-style pulsating sparkle-burst — an inline SVG (8 tapered spokes) that
// slowly rotates and breathes via CSS (.pulse-star). Inherits `currentColor`.
function PulseStar({ size = 17, className = "" }) {
  return (
    <span className={`pulse-star ${className}`} style={{ width: size, height: size }} aria-hidden="true">
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <path key={a} d="M12 1.8 Q 13.25 8 12 12 Q 10.75 8 12 1.8 Z" transform={`rotate(${a} 12 12)`} />
        ))}
      </svg>
    </span>
  );
}

// Marker/formatting tokens that must be revealed atomically, so the typewriter
// never renders a half-typed "@@sp" / "%%Matc" / "**82".
const ATOMIC_TOKEN = /(@@spin@@|%%[^%]*%%|\*\*[^*]*\*\*)/g;
function safeCut(text, n) {
  ATOMIC_TOKEN.lastIndex = 0;
  let m;
  while ((m = ATOMIC_TOKEN.exec(text))) {
    const s = m.index, e = m.index + m[0].length;
    if (n > s && n < e) return e;   // inside a token → snap to its end
    if (s >= n) break;
  }
  return n;
}

// Reveals assistant narration at ~250 WPM (≈20.8 chars/sec, ~48ms/char), capped
// so long messages still finish within ~6s. Always types toward the LATEST
// text, so a message that keeps updating in place (the live "Reading pages"
// counter) still tracks. Once fully revealed the id is recorded so background
// re-renders / re-mounts show it instantly instead of re-typing.
function Typewriter({ text, isUser, animate, msgId, onType, onDone }) {
  const done0 = !msgId || typedMsgIds.has(msgId);
  const [n, setN] = useState(done0 ? text.length : 0);
  const doneRef = useRef(done0);
  const textRef = useRef(text);
  textRef.current = text;
  useEffect(() => {
    if (doneRef.current) return;
    let cancelled = false, timer;
    let per = 1000 / ((250 * 5) / 60);
    if (text.length * per > 6000) per = 6000 / Math.max(text.length, 1);
    let i = 0;
    const finish = () => {
      if (msgId) typedMsgIds.add(msgId);
      doneRef.current = true;
      onDone && onDone();   // let the next queued message start typing
    };
    const step = () => {
      if (cancelled) return;
      const full = textRef.current;
      i = safeCut(full, i + 1);
      setN(i);
      onType && onType();
      if (i >= full.length) { finish(); return; }
      timer = setTimeout(step, per);
    };
    timer = setTimeout(step, per);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const shown = doneRef.current ? text : textRef.current.slice(0, n);
  return <FormattedContent content={shown} isUser={isUser} animate={animate} />;
}

function FormattedContent({ content, isUser, animate = false }) {
  if (isUser) return content;
  const inline = (line) => {
    // %%word%% → gradient shine while the step is live, plain bold once done.
    let f = line.replace(/%%(.*?)%%/g, animate
      ? '<span class="text-shine">$1</span>'
      : '<b class="text-[#09090B]">$1</b>');
    // @@spin@@ → rotating spinner while live, a green check once done.
    f = f.replace(/@@spin@@/g, animate
      ? '<span class="spin-icon align-[-2px] mr-1.5"></span>'
      : '<span class="mr-1.5 text-[#00C04B] font-bold">&#x2713;</span>');
    f = f.replace(/\*\*(.*?)\*\*/g, '<b class="text-[#09090B]">$1</b>');
    f = f.replace(/`(.*?)`/g, '<code class="font-mono-data bg-[#6043EF]/10 px-1.5 py-0.5 text-[#6043EF] text-[15px]">$1</code>');
    if (f.trimStart().startsWith("- ") || f.trimStart().startsWith("* ")) {
      f = f.replace(/^(\s*)[*-]\s/, '$1<span class="text-[#6043EF] font-bold mr-1.5">&#x2022;</span>');
    }
    return f;
  };
  const isTableRow = (l) => l.includes("|") && l.trim().startsWith("|");
  const isSep = (cells) => cells.every((c) => /^[-:]+$/.test(c));
  const cellsOf = (l) => l.split("|").filter((c) => c.trim()).map((c) => c.trim());

  const lines = content.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    if (isTableRow(lines[i])) {
      const block = [];
      while (i < lines.length && isTableRow(lines[i])) { block.push(lines[i]); i++; }
      const rows = block.map(cellsOf).filter((c) => c.length >= 2 && !isSep(c));
      if (rows.length) {
        const [head, ...body] = rows;
        out.push(
          <div key={`t${i}`} className="my-2 overflow-x-auto rounded-lg border border-zinc-200">
            <table className="w-full text-left text-[13px] font-['IBM_Plex_Mono'] border-collapse">
              <thead>
                <tr className="bg-zinc-100 text-zinc-600">
                  {head.map((h, j) => (
                    <th key={j} className="text-left font-bold px-3 py-1.5 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {body.map((r, ri) => (
                  <tr key={ri} className="hover:bg-zinc-50">
                    {head.map((_, ci) => (
                      <td key={ci} className="text-left px-3 py-1.5 whitespace-nowrap text-[#09090B]">{r[ci] ?? ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }
    out.push(<span key={i} className="block" dangerouslySetInnerHTML={{ __html: inline(lines[i]) || "&nbsp;" }} />);
    i++;
  }
  return out;
}
